/**
 * Keeps a console session alive past the 60-minute id token (SPEC decision 43): an expired token is renewed with the
 * session's refresh token, so the person signs in again only when the refresh token ends (Cognito's
 * `refresh_token_validity`, `console_session_hours` in Terraform, 8 h by default). Pure: the UserManager is passed in.
 */
import type { User } from 'oidc-client-ts';

export interface RenewableSession {
  getUser(): Promise<User | null>;
  signinSilent(): Promise<User | null>;
}

const usable = (u: User): boolean => !u.expired && Boolean(u.id_token);

let renewing: Promise<User | null> | null = null;

/** One refresh at a time: the calls that find the token expired together share it. A refused refresh means null. */
function renewOnce(um: RenewableSession): Promise<User | null> {
  renewing ??= um.signinSilent().then((u) => (u && usable(u) ? u : null), () => null).finally(() => { renewing = null; });
  return renewing;
}

/** The signed-in user with a valid id token, renewed first when it expired; null when there is none or it cannot be. */
export async function liveUser(um: RenewableSession): Promise<User | null> {
  const u = await um.getUser();
  if (u && usable(u)) return u;
  return u?.refresh_token ? renewOnce(um) : null;
}
